// Standalone auth/token module — NO React. Source of truth for tokens so both the React
// AuthContext (UI state) and any non-hook fetch layer share one refresh path.
//
// Ported from pollis-campaign-os/src/lib/auth.js and pointed at the same pollis-auth API.
// The campaign-selection logic is dropped — LandPilot has no campaigns.
//
// Storage keys are lp_* rather than pp_*: localStorage is per-origin, so landpilot.co and
// pollis.us cannot share a token anyway, and distinct keys stop a stale Pollis token from
// being picked up if the apps are ever served from one host. Cross-app handoff still works
// through the ?token=&uid= magic link, which is origin-independent.
const API_URL = process.env.REACT_APP_API_URL || "https://api.pollis.us";

const LS_ACCESS = "lp_access_token";
const LS_REFRESH = "lp_refresh_token";

// Parse a response body as JSON, but degrade an HTML error page into a clean message
// instead of the opaque "Unexpected token '<'" the browser throws on JSON.parse.
async function parseJsonSafe(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      res.ok
        ? "Unexpected server response."
        : `Sign in failed (server error ${res.status}).`,
    );
  }
}

// ── JWT helpers ────────────────────────────────────────────────────────────────
export function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
}
function isExpired(token) {
  const p = parseJwt(token);
  if (!p?.exp) return true;
  return p.exp * 1000 < Date.now() + 30000; // 30s buffer
}

// ── Token storage ──────────────────────────────────────────────────────────────
export const getAccessToken = () => localStorage.getItem(LS_ACCESS);
export const getRefreshToken = () => localStorage.getItem(LS_REFRESH);
export function storeTokens(access, refresh) {
  if (access) localStorage.setItem(LS_ACCESS, access);
  if (refresh) localStorage.setItem(LS_REFRESH, refresh);
}
export function clearTokens() {
  localStorage.removeItem(LS_ACCESS);
  localStorage.removeItem(LS_REFRESH);
}

// ── Session-expired signal ─────────────────────────────────────────────────────
// A 401 that SURVIVES a refresh attempt means the session is genuinely dead, not a blip.
// This module has no React, so it publishes the fact and AuthContext subscribes; the app
// then drops to sign-in instead of rendering an error over a shell the user can't use.
//
// Deliberately NOT fired on a network error during refresh: a dropped connection is not an
// expired session, and logging someone out because the wifi hiccupped is worse than an error.
let sessionExpiredHandlers = [];
export function onSessionExpired(fn) {
  sessionExpiredHandlers.push(fn);
  return () => {
    sessionExpiredHandlers = sessionExpiredHandlers.filter((h) => h !== fn);
  };
}
function notifySessionExpired() {
  clearTokens();
  sessionExpiredHandlers.forEach((fn) => {
    try {
      fn();
    } catch {
      /* a listener must never break the fetch */
    }
  });
}

// ── Refresh (single-flight) ────────────────────────────────────────────────────
let refreshing = null;
async function doRefresh() {
  const rt = getRefreshToken();
  if (!rt) return null;
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch(`${API_URL}/auth/token/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) {
        clearTokens();
        return null;
      }
      applyTokens(await res.json());
      return getAccessToken();
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

// Pull tokens out of an /auth/* response; the refresh token rides the JWT `ref` claim.
function applyTokens(data) {
  const access = data.token ?? data.accessToken;
  const refresh = data.refreshToken ?? parseJwt(access)?.ref ?? null;
  storeTokens(access, refresh);
}

// ── Valid access token (refreshing if near expiry) ─────────────────────────────
export async function getValidToken() {
  const at = getAccessToken();
  if (at && !isExpired(at)) return at;
  return doRefresh();
}

// ── Authenticated fetch — Bearer, refresh once on 401, retry ───────────────────
export async function authFetch(url, options = {}) {
  const token = await getValidToken();
  const isForm =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  const build = (t) => ({
    ...options,
    headers: {
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      ...(options.headers || {}),
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
  });
  let res = await fetch(url, build(token));
  if (res.status === 401) {
    const fresh = await doRefresh();
    if (fresh) res = await fetch(url, build(fresh));
    if (res.status === 401) notifySessionExpired();
  }
  return res;
}

// ── Auth actions ───────────────────────────────────────────────────────────────
export async function login(payload) {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data.error || "Login failed");
  applyTokens(data);
  return data;
}

export async function sendMagicLink(payload) {
  const res = await fetch(`${API_URL}/auth/magic/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data.error || "Could not send link");
  return data;
}

// SMS one-time code. The PIN is then redeemed through /auth/login as { phone, pin } —
// there is no separate verify endpoint.
export async function sendPin(payload) {
  const res = await fetch(`${API_URL}/auth/pin/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data.error || "Could not send PIN");
  return data;
}

// Handles the ?token=&uid= handoff (and the legacy ?magic_token=).
export async function verifyMagicToken(token, uid) {
  const res = await fetch(`${API_URL}/auth/magic/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, ...(uid != null ? { uid } : {}) }),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data.error || "Invalid link");
  applyTokens(data);
  return data;
}

export async function fetchMe() {
  const token = await getValidToken();
  if (!token) return null;
  const res = await authFetch(`${API_URL}/auth/me`);
  if (!res.ok) return null;
  return res.json();
}

export function logout() {
  clearTokens();
}
